import axios from 'axios';
import { getAuthInstance } from '../auth';
import { IDigitalAsset } from '../store/interfaces/IDigitalAsset';
import { IDigitalAssetUpload } from '../store/interfaces/IDigitalAssetUpload';

const FOUR_K_RES_WIDTH = 3840;
const FOUR_K_RES_HEIGHT = 2160;

export interface IDigitalAssetUploadValidationResult {
    isValid: boolean;
    isVideoFile: boolean;
    invalidReason?: string;
}

export interface IDigitalAssetUploadService {
    validateFileUpload(file: any): Promise<IDigitalAssetUploadValidationResult>;
    getFilePreview(file: any): Promise<string | ArrayBuffer | null>;
    upload(digitalAssetUpload: IDigitalAssetUpload): Promise<IDigitalAsset>;
}

const ACCEPTED_FILETYPES = [
    "image/png", "image/jpg", "image/jpeg", "video/mp4"
]

const FILE_SIZE_LIMIT_MB = 300;

export class DigitalAssetUploadService implements IDigitalAssetUploadService {
    private _reader: FileReader;
    constructor() {
        this._reader = new FileReader();
    }

    public async validateFileUpload(file: File, allowVideo = true, sizeLimitMB: number = FILE_SIZE_LIMIT_MB, availableStorage = 0): Promise<IDigitalAssetUploadValidationResult> {
        const mediaUploadValidationResult: IDigitalAssetUploadValidationResult = {
            isValid: true,
            isVideoFile: false
        };

        if(!file) {
            mediaUploadValidationResult.isValid = false
            mediaUploadValidationResult.invalidReason = 'Unable to evaluate file';
            return mediaUploadValidationResult;
        }

        if (!ACCEPTED_FILETYPES.some((ft) => ft === file.type)) {
            mediaUploadValidationResult.isValid = false
            mediaUploadValidationResult.invalidReason = 'Invalid file type';
            return mediaUploadValidationResult;
        }

        if (file.type.match("video/mp4")) {
            mediaUploadValidationResult.isVideoFile = true;
        }

        if(!allowVideo && mediaUploadValidationResult.isVideoFile) {
            mediaUploadValidationResult.isValid = false
            mediaUploadValidationResult.invalidReason = 'Invalid file type (video files not accepted)';
            return mediaUploadValidationResult;
        }

        const fileSizeInMB = ((file.size/1024)/1024);
        const availableStorageInMb = ((availableStorage / 1024) / 1024);

        if(availableStorage && availableStorage > 0 && availableStorageInMb < fileSizeInMB) {
            mediaUploadValidationResult.isValid = false
            mediaUploadValidationResult.invalidReason = `Your organization has reached its storage limit.`;
            return mediaUploadValidationResult;
        }

        if(sizeLimitMB < fileSizeInMB) {
            mediaUploadValidationResult.isValid = false
            mediaUploadValidationResult.invalidReason = `File size of ${fileSizeInMB.toFixed(2)}MB is over the ${sizeLimitMB}MB limit`;
            return mediaUploadValidationResult;
        }

        return mediaUploadValidationResult
    }

    public getFilePreview(file: File): Promise<string | ArrayBuffer | null> {
        return new Promise((resolve, reject) => {
          try {
            // use a new FileReader instance
            const reader = new FileReader();
      
            reader.onloadend = () => {
              resolve(reader.result);
            };
      
            reader.onerror = () => {
              reject('Failed to read file');
            };
      
            // clone the file to avoid "busy" errors if same blob is read again
            const fileCopy = file.slice(0, file.size, file.type);
            reader.readAsDataURL(fileCopy);
          } catch (err) {
            reject(err);
          }
        });
      }
      

    async validateResolution(previewUrl: string, fileType: 'image' | 'video'): Promise<{ isValid: boolean, invalidReason?: string }> {
        if (fileType == 'image') {
            return new Promise((resolve) => {
                const image = new Image();
                image.onload = () => {
                    if (image.width > FOUR_K_RES_WIDTH || image.height > FOUR_K_RES_HEIGHT) {
                        resolve({ isValid: false, invalidReason: 'This file exceeds the 4K resolution limit.' })
                    } else {
                        resolve({ isValid: true })
                    }
                };
                image.onerror = () => {
                    resolve({ isValid: false, invalidReason: `Unable to Read Image` })
                }
                image.src = previewUrl;
            })
        }
        else if (fileType == 'video') {
            return new Promise((resolve) => {
                const video = document.createElement("video");
                video.preload = "metadata";
                video.onloadedmetadata = () => {
                    if (video.videoWidth > FOUR_K_RES_WIDTH || video.videoHeight > FOUR_K_RES_HEIGHT) {
                        resolve({ isValid: false, invalidReason: 'This file exceeds the 4K resolution limit.' })
                    } else {
                        resolve({ isValid: true })
                    }
                };
                video.onerror = () => {
                    resolve({ isValid: false, invalidReason: 'Unable to read Video' });
                }
                video.src = previewUrl;
            })
        } 
        // NOTE: Added below condition to avoid Typescript error
        else {
            return Promise.resolve({ isValid: true });
        }
    }

    public async upload(digitalAssetUpload: IDigitalAssetUpload): Promise<IDigitalAsset> {
        const digitalAssetsURL = `${ process.env.VUE_APP_API_HOST }/api/digitalassets/upload`;
        const authHeader = await getAuthInstance().getAxiosHeader();

        const formData = new FormData();
        formData.append('assetQuotaType', JSON.stringify(digitalAssetUpload.assetQuotaType));
        formData.append('file', digitalAssetUpload.file);
        formData.append('secure', JSON.stringify(digitalAssetUpload.secure));
        formData.append('generateThumb', JSON.stringify(digitalAssetUpload.generateThumb));

        if(digitalAssetUpload.imageHeight) {
            formData.append('imageHeight', JSON.stringify(digitalAssetUpload.imageHeight));
        }
        if(digitalAssetUpload.imageWidth) {
            formData.append('imageWidth', JSON.stringify(digitalAssetUpload.imageWidth));
        }
        if(digitalAssetUpload.fileName) {
            formData.append('fileName', JSON.stringify(digitalAssetUpload.fileName));
        }
        if(digitalAssetUpload.id) {
            formData.append('id', digitalAssetUpload.id);
        }

        const axiosResponse = await axios.post(digitalAssetsURL, formData, authHeader);
        return axiosResponse.data;
    }
}

export default new DigitalAssetUploadService();